import { useCallback } from 'react';
import { useReorderClipMutation } from '../../operation.generated';
import { ClipObject } from '../../../../context/clips';
import generateSequence from '../../../../util/generateSequence';
import { toast } from '../../../Toaster';
import { getApolloMutationErrorMessage } from '../../../../util/apolloError';

// temp sequence util is moved outside of hook to be exposed in testing suite
export const getTempSequence = (inputs: { precedingClip?: ClipObject }) => {
  const { precedingClip } = inputs;
  let tempSequence: string;
  if (precedingClip) {
    // Making the sequence number be smaller than the preceding clip so that
    // the clip ends up below the preceding clip in the clips list
    tempSequence = (BigInt(precedingClip.sequence) - BigInt(10)).toString();
  } else {
    // Assigning a sequence value larger than the rest of the clips to the clip
    // so that it ends up at the top of the clips list
    tempSequence = generateSequence();
  }
  return tempSequence;
};

const useUpdateClipOrdering = () => {
  const [reorderClip] = useReorderClipMutation();

  const updateClipOrdering = useCallback(
    async (inputs: {
      clip: ClipObject;
      newIndex: number;
      precedingClip?: ClipObject;
    }) => {
      // Temporarily setting a sequence value for the specific clip before actually
      // getting the real sequence value from the backend, so that we can show the reordering
      // taking effect in real-time. Later, when the call to the backend is complete, we can
      // replace the temp sequence value with the actual one.
      const sequence = getTempSequence({
        precedingClip: inputs.precedingClip,
      });
      try {
        await reorderClip({
          variables: {
            inputs: {
              precedingClipId: inputs.precedingClip
                ? inputs.precedingClip.id
                : undefined,
              clipId: inputs.clip.id,
              projectId: inputs.clip.projectId,
            },
          },
          optimisticResponse: {
            reorderClip: {
              __typename: 'ReorderClipMutationResponse',
              code: 'graphql_mutation_success',
              message: 'Reordered clip successfully',
              success: true,
              clip: {
                __typename: 'Clip',
                id: inputs.clip.id,
                sequence,
              },
            },
          },
          update: (proxy, { data }) => {
            if (!data?.reorderClip.success) {
              const errorMessage =
                data?.reorderClip.message ?? 'Failed to reorder clip';
              toast({
                message: errorMessage,
                type: 'error',
              });
            }
          },
        });
      } catch (error) {
        toast({
          message: getApolloMutationErrorMessage(error, 'Failed to reorder clip'),
          type: 'error',
        });
      }
    },
    [reorderClip]
  );
  return updateClipOrdering;
};

export default useUpdateClipOrdering;
